import { isString, includes, isArray } from "lodash";
import DeviceUtils from "@utils/device.utils";

/**
 * Utilities for working with the Document Object Model (DOM).
 * @module DOMUtils
 */
const DOMUtils = {
  /**
   * Returns the tag name of the specified element.
   * @static
   * @function tag
   * @param {Element} el - The element to check the tag of.
   * @param {Boolean} [lowercase=true] - Whether to normalize the tag name for comparison as lowercase.
   * @example
   * DOMUtils.tag(document.querySelector('.foo'));
   * @returns {String} - The tag of the specified element.
   */
  tag(el, lowercase = true) {
    return lowercase ? el.nodeName.toLowerCase() : el.nodeName;
  },

  /**
   * Returns the element(s) matching the specified id selector.
   * @static
   * @function elById
   * @param {String} id - The id of the element to query.
   * @example
   * DOMUtils.elById('foo');
   * @returns {Element} - The element matching the id.
   */
  elById(id) {
    const el = document.getElementById(id);

    if (el) {
      return el;
    } else {
      return undefined;
    }
  },

  /**
   * Returns the element(s) matching the specified selector.
   * @static
   * @function el
   * @param {String} selector - The selector of the element to query.
   * @example
   * DOMUtils.el('.foo');
   * @returns {Element|Array} - The element or array of elements matching the selector.
   */
  el(selector) {
    const els = document.querySelectorAll(selector);

    if (els.length === 0) {
      return null;
    } else if (els.length > 1) {
      return els;
    } else {
      return els[0];
    }
  },

  /**
   * Returns the element(s) matching the specified selector on specified container.
   * @static
   * @function elOf
   * @param {Element} container - The container of selector to find
   * @param {String} selector - The selector of the element to query.
   * @example
   * DOMUtils.elOf(document.querySelector('.bar'), '.foo');
   * @returns {Array<Element>} - The array of elements matching the selector.
   */
  elOf(container: Element, selector: string): Element[] {
    const elements: NodeListOf<Element> = container.querySelectorAll(selector);
    return Array.from(elements);
  },

  /**
   * Returns the element's parent or the closest ancestor matching the specified selector if provided.
   * @static
   * @function parent
   * @param {String|Element} item - The selector or element to find the parent of.
   * @param {String} [parentSelector] - The selector of the ancestor to find.
   * @example
   * DOMUtils.parent('.foo', '.bar');
   * @returns {Element} - The parent of the item.
   */
  parent(item, parentSelector) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return undefined;
    }

    if (parentSelector) {
      const allParents = document.querySelectorAll(parentSelector);
      let currentParent = el.parentNode;

      while (currentParent && !includes(allParents, currentParent)) {
        currentParent = currentParent.parentNode;
      }

      return currentParent;
    } else {
      return el.parentNode;
    }
  },

  /**
   * Returns the attribute of the specified element.
   * @static
   * @function attr
   * @param {String|Element} item - The selector or element to query.
   * @param {String} attribute - The attribute to query.
   * @example
   * DOMUtils.attr('.foo', 'data-baz');
   * @returns {String} - The value of the attribute.
   */
  attr(item, attribute) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    return el.getAttribute(attribute);
  },

  /**
   * set the attribute of the specified element.
   * @static
   * @function attr
   * @param {String|Element} item - The selector or element to query.
   * @param {String} attribute - The attribute to set.
   * @param {String} value - The value to set.
   * @example
   * DOMUtils.setAttr('.foo', 'data-count', 1);
   */
  setAttr(item, attribute, value) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    el.setAttribute(attribute, value);
  },

  /**
   * Remove the attribute of the specified element.
   * @param {String|Element} item - The selector or element to query.
   * @param {String} attribute - The attribute to remove.
   */
  removeAttr(item, attribute) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    el.removeAttribute(attribute);
  },

  /**
   * Adds a class to the specified element.
   * @static
   * @function addClass
   * @param {String|Element} item - The selector or element to add the class to.
   * @param {String} className - The class to add.
   * @example
   * DOMUtils.addClass(evt.currentTarget, 'foo');
   * @returns {void}
   */
  addClass(item, className) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    if (el.classList) {
      el.classList.add(className);
    } else {
      el.className += ` ${className}`;
    }
  },

  /**
   * Removes a class from the specified element.
   * @static
   * @function removeClass
   * @param {String|Element} item - The selector or element to remove the class from.
   * @param {String} className - The class to remove.
   * @example
   * DOMUtils.removeClass(evt.currentTarget, 'foo');
   * @returns {void}
   */
  removeClass(item, className) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return;
    }

    if (el.classList) {
      el.classList.remove(className);
    } else {
      el.className = el.className.replace(
        new RegExp(`(^|\\b)${className.split(" ").join("|")}(\\b|$)`, "gi"),
        " ",
      );
    }
  },

  /**
   * Checks if an element has a specified class.
   * @static
   * @function hasClass
   * @param {String|Element} item - The selector or element to check.
   * @param {String} className - The class to check for.
   * @example
   * DOMUtils.hasClass(evt.currentTarget, 'foo');
   * @returns {Boolean} - Whether the element has the class or not
   */
  hasClass(item, className) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return false;
    }

    if (el.classList) {
      return el.classList.contains(className);
    } else if (el.className) {
      return includes(el.className.split(" "), className);
    } else {
      return false;
    }
  },

  /**
   * Get the height of the specified element.
   * @static
   * @function height
   * @param {String|Element} item - The selector or element to find the height of.
   * @param {Boolean} [includeMargin=true] - Whether to include the margins in the calculation effectively getting the outerHeight.
   * @param {Boolean} [includePadding=true] - Whether to include the padding in the calculation.
   * @example
   * DOMUtils.height('.foo');
   * @returns {Number} - The height of the element.
   */
  height(item, includeMargin = true, includePadding = true) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return 0;
    }

    const { marginTop, marginBottom, paddingTop, paddingBottom } = getComputedStyle(el);
    let height = el.offsetHeight;

    if (includeMargin) {
      height += parseInt(marginTop, 10) + parseInt(marginBottom, 10);
    }

    if (!includePadding) {
      height -= parseInt(paddingTop, 10) + parseInt(paddingBottom, 10);
    }

    return height;
  },

  /**
   * Get the width of the specified element.
   * @static
   * @function width
   * @param {String|Element} item - The selector or element to find the width of.
   * @param {Boolean} [includeMargin=true] - Whether to include the margins in the calculation effectively getting the outerWidth.
   * @param {Boolean} [includePadding=true] - Whether to include the padding in the calculation.
   * @example
   * DOMUtils.width('.foo');
   * @returns {Number} - The width of the element.
   */
  width(item, includeMargin = true, includePadding = true) {
    const el = isString(item) ? DOMUtils.el(item) : item;

    if (!el) {
      return 0;
    }

    const { marginLeft, marginRight, paddingLeft, paddingRight } = getComputedStyle(el);
    let width = el.offsetWidth;

    if (includeMargin) {
      width += parseInt(marginLeft, 10) + parseInt(marginRight, 10);
    }

    if (!includePadding) {
      width -= parseInt(paddingLeft, 10) + parseInt(paddingRight, 10);
    }

    return width;
  },

  /**
   * Removes element(s) from the DOM.
   * @static
   * @function remove
   * @param {String|Element|Array} items - The selector or element(s) to remove from the DOM.
   * @example
   * DOMUtils.remove('.class-to-remove');
   * @returns {void}
   */
  remove(items) {
    const el = isString(items) ? DOMUtils.el(items) : items;

    if (!el) {
      return;
    }

    if (isArray(el)) {
      el.each((thisEl) => {
        thisEl.remove();
      });
    } else {
      el.remove();
    }
  },

  createDivInBody(attribute) {
    const div = document.createElement("div", attribute);
    document.body.appendChild(div);
    return div;
  },

  /**
   * Create DOM element, with attributes
   * @static
   * @function createElement
   * @param {String} element - the element string
   * @param {Object} attributes - object containing attributes
   * @example
   * DOMUtils.createElement("div", { class: "class-to-add", data-uuid: "uuid" });
   * @returns {Element}
   */
  createElement(element, attributes = {}) {
    const el = document.createElement(element);
    for (const key in attributes) {
      if (attributes[key] !== undefined) {
        el.setAttribute(key, attributes[key]);
      }
    }
    return el;
  },

  /**
   * Insert new node right after the existing node
   * @param {Element} newNode
   * @param {Element} existingNode
   */
  insertAfter(newNode, existingNode) {
    existingNode.parentNode.insertBefore(newNode, existingNode.nextSibling);
  },

  /**
   * Get next element by class name
   * @param {Element} elem
   * @param {String} className
   */
  nextElementByClass(elem, className) {
    let next = elem.nextElementSibling;
    while (next && !next.classList.contains(className)) {
      next = next.nextElementSibling;
    }
    return next; // return if found, or null
  },
  /**
   * Get next element by class name
   * @param {Element} elem
   * @param {String} className
   */
  setCSSVariable(variable, value) {
    const r = document.querySelector(":root") as HTMLElement | null;
    r?.style.setProperty(variable, value);
  },
  setCSSVariableInElement(element, variable, value) {
    element.style.setProperty(variable, value);
  },
  removeCSSVariableInElement(element, variable) {
    element.style.removeProperty(variable);
  },
  addBeforeUnloadEventListener(callback) {
    if (DeviceUtils.isIos()) {
      window.addEventListener("pagehide", callback);
    } else {
      window.addEventListener("beforeunload", callback);
    }
  },
  removeBeforeUnloadEventListener(callback) {
    if (DeviceUtils.isIos()) {
      window.removeEventListener("pagehide", callback);
    } else {
      window.removeEventListener("beforeunload", callback);
    }
  },
  isWhitelistedPluginNotPresent() {
    const wirisModal = DOMUtils.elOf(document, ".wrs_modal_dialogContainer.wrs_stack");
    const audioRecorder = DOMUtils.elOf(document, ".audio-recorder");
    const fileUploaderModal = DOMUtils.elOf(document, ".file-uploader-modal");
    const drawingModal = DOMUtils.elOf(document, ".drawing-canvas-modal");
    return (
      wirisModal.length === 0 &&
      audioRecorder.length === 0 &&
      fileUploaderModal.length === 0 &&
      drawingModal.length === 0
    );
  },
  isRichTextWhiteListedClickedElement(clickElement) {
    //DRAWING/IMG
    if (
      DOMUtils.hasClass(clickElement, "zoom-in") ||
      DOMUtils.hasClass(clickElement, "zoom-out") ||
      DOMUtils.hasClass(clickElement, "zoom-reset") ||
      DOMUtils.parent(clickElement, "button.zoom-in, button.zoom-out, button.zoom-reset")
    ) {
      return true;
    }
    //DOWNLOAD BUTTON
    if (
      (DOMUtils.hasClass(clickElement, "download") && DOMUtils.hasClass(clickElement, "action")) ||
      DOMUtils.parent(clickElement, "button.download.action")
    ) {
      return true;
    }
    //VIDEO/AUDIO
    if (DOMUtils.hasClass(clickElement, "plyr") || DOMUtils.parent(clickElement, ".plyr")) {
      return true;
    }
    //GOOGLE
    if (
      DOMUtils.hasClass(clickElement, "openInNewTab") ||
      DOMUtils.parent(clickElement, "button.openInNewTab") ||
      DOMUtils.hasClass(clickElement, "bx--overflow-menu__trigger") ||
      DOMUtils.parent(clickElement, ".bx--overflow-menu__trigger")
    ) {
      return true;
    }

    return false;
  },
  isClickingWithinOwnMargin(event) {
    if (event && event instanceof PointerEvent) {
      const target = event.target as HTMLElement;
      const targetRect = target.getBoundingClientRect();

      return (
        event.clientX < targetRect.left + target.clientLeft ||
        event.clientX > targetRect.right - target.clientLeft ||
        event.clientY < targetRect.top + target.clientTop ||
        event.clientY > targetRect.bottom - target.clientTop
      );
    }
    return false;
  },
  replaceComponentWrapperWithComponentElement(component) {
    component.$el.parentNode.replaceWith(component.$el);
  },
  parseHtmlToPlainText(content: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    return doc.body.textContent?.replace(/[\s\n]+/g, " ").trim();
  },
};

export default DOMUtils;
